import { useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import type { FlagsStateContainer } from '@confluence/flags';

const i18n = defineMessages({
	allCommentsMarkedAsResolved: {
		id: 'comments-panel.comment-bulk-actions.all-comments-marked-as-resolved',
		defaultMessage: 'All comments marked as resolved',
		description:
			'The label for a flag that notifies the user that the bulk resolve operation was successful',
	},
	allCommentsMarkedAsRead: {
		id: 'comments-panel.comment-bulk-actions.all-comments-marked-as-read',
		defaultMessage: 'All comments marked as read',
		description:
			'The label for a flag that notifies the user that the bulk read operation was successful',
	},
	commentsFailedToResolve: {
		id: 'comments-panel.comment-bulk-actions.comments-failed-to-resolve',
		defaultMessage: 'Comments failed to resolve. Please try again.',
		description:
			'The label for a flag that notifies the user that the bulk resolve operation was unsuccessful',
	},
	failedToMarkAllCommentsAsRead: {
		id: 'comments-panel.comment-bulk-actions.failed-to-mark-all-comments-as-read',
		defaultMessage: 'Failed to mark all comments as read. Please try again.',
		description:
			'The label for a flag that notifies the user that the bulk read operation was unsuccessful',
	},
	retryNow: {
		id: 'comments-panel.comment-bulk-actions.retry-now',
		defaultMessage: 'Retry now',
		description: 'The label for a button that retries the failed operation',
	},
});

const MAXIMUM_BULK_ACTION_RETRIES = 1;
export const READ = 'read';
export const RESOLVE = 'resolve';

export type ACTION = typeof READ | typeof RESOLVE;

const getSuccessFlagId = (action: ACTION) =>
	`comments-panel.comment-bulk-actions.mark-all-as-${action}-success`;

const getFailureFlagId = (action: ACTION) =>
	`comments-panel.comment-bulk-actions.mark-all-as-${action}-failure`;

const getSuccessMessage = (action: ACTION) => {
	switch (action) {
		case READ:
			return i18n.allCommentsMarkedAsRead;
		case RESOLVE:
			return i18n.allCommentsMarkedAsResolved;
		default:
			throw new Error('Invalid action');
	}
};

const getFailureMessage = (action: ACTION) => {
	switch (action) {
		case READ:
			return i18n.failedToMarkAllCommentsAsRead;
		case RESOLVE:
			return i18n.commentsFailedToResolve;
		default:
			throw new Error('Invalid action');
	}
};

export type Flags = {
	showSuccess: () => void;
	showFailure: (handleRetry: () => void) => void;
};

export const useCommentBulkOperationsFlags = (action: ACTION, flags: FlagsStateContainer) => {
	const retryCountRef = useRef(0);
	const { formatMessage } = useIntl();

	const showSuccess = () => {
		const successFlagId = getSuccessFlagId(action);
		const title = getSuccessMessage(action);

		void flags.showSuccessCircleFlag({
			id: successFlagId,
			title: formatMessage(title),
			isAutoDismiss: true,
		});
	};

	const showFailure = (handleRetry: () => void) => {
		const failureFlagId = getFailureFlagId(action);
		const title = getFailureMessage(action);

		const actions =
			retryCountRef.current < MAXIMUM_BULK_ACTION_RETRIES
				? [
						{
							content: formatMessage(i18n.retryNow),
							onClick: () => {
								void handleRetry();
								void flags.hideFlag(failureFlagId);
								retryCountRef.current += 1;
							},
						},
					]
				: [];

		void flags.showErrorFlag({
			id: failureFlagId,
			title: formatMessage(title),
			isAutoDismiss: true,
			actions,
		});
	};

	return {
		retryCountRef,
		showSuccess,
		showFailure,
	};
};
